/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from "react";

import { Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";

import { ContextProvider } from "./context-index";
import {
  GridProps,
  Constants,
  SortType,
  SortColumn,
  Column,
  RowActionsExtra,
  RowActions,
} from "./export";
import * as GridActions from "./GridActions";
import GridBody from "./GridBody";
import GridBodyLoading from "./GridBodyLoading";
import GridFooter from "./GridFooter";
import GridHeader, { GridHeaderHandles } from "./GridHeader";
import TopPanel from "./TopPanel";

import "./styles.scss";

const GridComponent = ({
  columns,
  data,
  keyExtractor,
  loadingData = false,
  emptyData,
  namePage,
  titlePage,
  rowActions,
  rowActionsExtra,
  generalActions,
  positionActionsColumn,
  pageSize,
  pageSizeList,
  onChangePageSize,
  totalItems,
  currentPage,
  onPreviousPage,
  onNextPage,
  onGoToPage,
  handleSelect,
  clearSelection,
  gridCellPadding,
  gridHeaderCellPadding,
  showSelection = true,
  showHeaderGrid = true,
  showFooterGrid = true,
  showFooterPageInfo = true,
  showFooterPaginationInfo = true,
  alwaysShowFooter,
  showSort = true,
  showColumnChooser = true,
  onSorting,
  hasComponent,
  component,
  selecteds = [],
  renderAfterRow,
  rowStyle,
  insideData,
  showActionLabel = true,
  onChangeColumns,
  stickyActions = false,
  style,
  footerStyle,
}: GridProps) => {
  const allSelectedRef = useRef<GridHeaderHandles>(null);

  const [renderData, setRenderData] = useState<any[]>(data);
  const [selectedList, setSelectedList] = useState<any[]>(selecteds);
  const [searching] = useState(false);
  const [endScrolled, setEndScrolled] = useState(!stickyActions);

  const scrollableDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRenderData(data);
  }, [data]);

  useEffect(() => {
    allSelectedRef.current?.verifyIfHasSelected(data.length, selectedList, searching);
  }, [selectedList]);

  const onHorizontalScrollTable = (event: any) => {
    if (!stickyActions) return;
    if (!scrollableDivRef.current) return;

    const scrollValue =
      Number(event.target.scrollLeft) + Number(scrollableDivRef?.current?.clientWidth);

    if (scrollValue >= scrollableDivRef?.current?.scrollWidth - 10) {
      if (!endScrolled) setEndScrolled(true);
    } else if (endScrolled) setEndScrolled(false);
  };

  useEffect(() => {
    if (clearSelection && clearSelection !== undefined) {
      setSelectedList([]);
    }
  }, [clearSelection]);

  function handleChangePage(option: string, numPage: number) {
    switch (option) {
      case "previous":
        onPreviousPage?.(numPage);
        break;
      case "next":
        onNextPage?.(numPage);
        break;
      case "goto":
        onGoToPage?.(numPage);
        break;
      case "first":
        onGoToPage?.(numPage);
        break;
      case "last":
        onGoToPage?.(numPage);
        break;
      default:
        break;
    }
  }

  function handleSelectRow(id: string | number, checked: boolean) {
    let array: Array<string | number> = [];

    array = checked
      ? [...selectedList.filter((i) => i !== id), id]
      : [...selectedList.filter((i) => i !== id)];
    setSelectedList(array);
  }

  function handleSelectAllRows(checked: boolean) {
    if (checked) {
      setSelectedList([...renderData.map((row, index) => getRowId(row, index))]);
    } else {
      setSelectedList([]);
    }
  }

  useEffect(() => {
    if (handleSelect) {
      handleSelect(selectedList);
    }
  }, [selectedList]);

  const getRowId = (row: any, index: number) => {
    return keyExtractor ? keyExtractor(row, index) : row && row?.id ? row?.id : index;
  };

  function handleSortingData(sorts: SortColumn[]) {
    const column = sorts[0].columnName;
    const { direction } = sorts[0];

    switch (direction) {
      case SortType.ASC:
        setRenderData([
          ...renderData.sort((a, b) =>
            a[column] < b[column] ? -1 : a[column] > b[column] ? 1 : 0
          ),
        ]);
        break;

      case SortType.DESC:
        setRenderData([
          ...renderData.sort((a, b) =>
            a[column] > b[column] ? -1 : a[column] < b[column] ? 1 : 0
          ),
        ]);
        break;
      default:
        break;
    }
  }

  const renderTopPanel = () => {
    if (showHeaderGrid) {
      return (
        <TopPanel
          columns={columns}
          actions={generalActions}
          titlePage={titlePage}
          selectedList={selectedList}
        />
      );
    }

    return null;
  };

  const renderFooterGrid = () => {
    if (showFooterGrid) {
      return (
        <GridFooter
          handleChangePage={handleChangePage}
          currentPage={currentPage}
          pageSize={pageSize || Constants.defaultPageSize}
          pageSizeList={pageSizeList}
          onChangePageSize={onChangePageSize}
          totalItems={totalItems || (data?.length ? data?.length : 0)}
          namePage={namePage}
          onGoToPage={onGoToPage}
          showFooterPageInfo={showFooterPageInfo}
          showFooterPaginationInfo={showFooterPaginationInfo}
          alwaysShowFooter={alwaysShowFooter}
          footerStyle={footerStyle}
        />
      );
    }

    return null;
  };

  const renderInsideGridBody = (row: any, index?: number) => {
    if (!insideData) return null;

    const { show, dataKey, rowActions, rowActionsExtra, rowStyle: subRowStyle } = insideData;

    if (!show) return null;

    const showTheInside = show instanceof Function ? show(row, index) : show;

    if (!showTheInside) return null;

    if (!dataKey || !row || !row[dataKey]) return null;

    const rows = row[dataKey];

    if (!rows || !rows.length) return null;

    return rows.map((subRow: any, subIndex: number) => {
      return (
        <GridBody
          row={subRow}
          columns={columns}
          index={subIndex}
          rowId={getRowId(subRow, subIndex)}
          key={getRowId(subRow, subIndex)}
          rowActions={rowActions}
          rowActionsExtra={rowActionsExtra}
          positionActions={positionActionsColumn || "left"}
          showSelection={false}
          selectedList={selectedList}
          handleSelectRow={handleSelectRow}
          length={rows.length}
          rowStyle={subRowStyle}
          isInside={true}
          endScrolled={stickyActions ? endScrolled : true}
          stickyActions={stickyActions}
          gridCellPadding={gridCellPadding}
        />
      );
    });
  };

  interface RenderGridDataProps {
    bodyData: any[];
    bodyColumns: Column[];
    enableGetRowSize?: boolean;
    pinnedTable?: boolean;
    bodyRowActionsExtra?: RowActionsExtra[];
    bodyRowActions?: RowActions;
  }

  const renderGridBody = ({
    bodyData,
    bodyColumns,
    enableGetRowSize,
    bodyRowActionsExtra,
    bodyRowActions,
    pinnedTable,
  }: RenderGridDataProps) => {
    if (loadingData) {
      return (
        <GridBodyLoading
          pageSize={pageSize || Constants.defaultPageSize}
          length={bodyData.length}
          showSelection={showSelection}
          rowActions={bodyRowActions}
          rowActionsExtra={bodyRowActionsExtra}
          positionActions={positionActionsColumn || "left"}
        />
      );
    }

    if (bodyData && bodyData?.length > 0) {
      return bodyData?.map((row, index) => {
        const afterRowContent = renderAfterRow && renderAfterRow(row, index);

        return (
          <React.Fragment key={getRowId(row, index)}>
            <GridBody
              columns={bodyColumns}
              handleSelectRow={handleSelectRow}
              row={row}
              rowActions={bodyRowActions}
              rowActionsExtra={bodyRowActionsExtra}
              positionActions={positionActionsColumn || "left"}
              selectedList={selectedList}
              showSelection={showSelection}
              index={index}
              length={bodyData.length}
              rowId={getRowId(row, index)}
              key={getRowId(row, index)}
              rowStyle={rowStyle}
              enableGetRowSize={enableGetRowSize}
              pinnedTable={pinnedTable}
              endScrolled={stickyActions ? endScrolled : true}
              stickyActions={stickyActions}
              gridCellPadding={gridCellPadding}
            />
            {renderInsideGridBody(row, index)}
            {afterRowContent && (
              <TableRow>
                <TableCell colSpan={bodyColumns.length + (showSelection ? 1 : 0)}>
                  {afterRowContent}
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        );
      });
    }

    return null;
  };

  const renderGridTable = () => {
    if (hasComponent && component && data && data?.length > 0) {
      return <div>{component(data)}</div>;
    }
    if (!loadingData && renderData && renderData?.length === 0) {
      if (emptyData && typeof emptyData === "string") {
        return (
          <div className="empty">
            <span>{emptyData}</span>
          </div>
        );
      }
      if (emptyData) {
        return emptyData();
      }
    }

    if (loadingData || (renderData && renderData?.length > 0)) {
      const pinnedColumns = columns.filter((column) => column.pinned) ?? [];
      const unpinnedColumns = columns.filter((column) => !column.pinned) ?? [];

      return (
        <div className="tableContainerX">
          <TableContainer
            onScroll={onHorizontalScrollTable}
            ref={scrollableDivRef}
            data-cy="table-container"
          >
            <Table
              stickyHeader={!!stickyActions}
              aria-label="sticky table"
              data-cy="table"
              size="small"
            >
              <GridHeader
                loadingData={loadingData}
                ref={allSelectedRef}
                columns={[...pinnedColumns, ...unpinnedColumns]}
                actions={rowActions}
                extraActions={rowActionsExtra}
                positionActions={positionActionsColumn || "left"}
                searching={searching}
                showSelection={showSelection}
                handleSelectAllRows={handleSelectAllRows}
                showSort={showSort}
                handleSortingData={onSorting || handleSortingData}
                showActionLabel={showActionLabel}
                enableGetHeaderHeight={!!pinnedColumns.length}
                showColumnChooser={showColumnChooser}
                endScrolled={stickyActions ? endScrolled : true}
                stickyActions={stickyActions}
                gridCellPadding={gridHeaderCellPadding}
              />
              <TableBody data-cy="table-body">
                {renderGridBody({
                  bodyData: renderData,
                  bodyColumns: [...pinnedColumns, ...unpinnedColumns],
                  bodyRowActions: rowActions,
                  bodyRowActionsExtra: rowActionsExtra,
                  enableGetRowSize: !!pinnedColumns.length,
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {pinnedColumns.length && !loadingData ? (
            <div className="pinnedTable">
              <TableContainer data-cy="table-container">
                <Table data-cy="table" size="small">
                  <GridHeader
                    loadingData={loadingData}
                    ref={allSelectedRef}
                    columns={pinnedColumns}
                    actions={rowActions}
                    extraActions={rowActionsExtra}
                    positionActions={positionActionsColumn || "left"}
                    searching={searching}
                    showSelection={showSelection}
                    handleSelectAllRows={handleSelectAllRows}
                    showSort={showSort}
                    handleSortingData={onSorting || handleSortingData}
                    showActionLabel={false}
                    pinnedTable={true}
                    showColumnChooser={false}
                    stickyActions={stickyActions}
                  />
                  <TableBody data-cy="table-body">
                    {renderGridBody({
                      bodyData: renderData,
                      bodyColumns: pinnedColumns,
                      pinnedTable: true,
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : (
            <></>
          )}
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    return (
      <>
        {renderGridTable()}
        {renderFooterGrid()}
      </>
    );
  };

  return (
    <ContextProvider columns={columns} onChangeColumns={onChangeColumns}>
      <div
        className="shipeezi-new-grid-component"
        id="shipeezi-new-grid-component"
        data-cy="shipeezi-new-grid"
        style={style}
      >
        {renderTopPanel()}
        {renderContent()}
      </div>
    </ContextProvider>
  );
};

export default GridComponent;
export * from "./export";

const Icons = {
  edit: GridActions.FiEdit,
  view: GridActions.FiEye,
  print: GridActions.FiPrinter,
  delete: GridActions.FiTrash2,
  email: GridActions.HiOutlineMail,
  status: GridActions.ChangeStatusButton,
};

export { Icons };
